// Step 2 (Social Post route) — CROP the product before compose.
//
// Background-removal (rembg) was dropped: the cutouts had visible/jagged
// edges and looked unprofessional. Instead the product is composited by
// OpenAI downstream (StepSocialFinal) — onto a flat slot color for plain
// templates, or into the scene for scene-merge templates. The model
// extracts + relights the product cleanly, so no client-side cutout is
// needed.
//
// This step's only job: let the user TRIM the product to a tight region
// (free rectangle). Why: a small/rectangular product in a large photo
// renders tiny or letterboxed once composited — a tight crop frames it so
// it fills the target. Cropping is OPTIONAL; the full image flows through
// if untouched.
//
// Output (onCutoutChange): the cropped product image as a data URL — a
// normal RGB image (NOT transparent). StepSocialFinal composes it.

import { useEffect, useRef, useState } from 'react';
import StepHeader from './StepHeader';

interface StepConfirmCutoutProps {
  productFile: File | null;
  /** Optional original preview (data URL). When absent we derive it
   *  from productFile. */
  productImage?: string;
  /** The cropped product image (data URL) held in parent state. Lets us
   *  restore the result when the user navigates back. */
  croppedImage: string | null;
  /** Called with the cropped product image (data URL), or null to clear. */
  onCutoutChange: (dataUrl: string | null) => void;
  /** /appv2's Section wrapper supplies its own header. */
  compact?: boolean;
}

type CropRect = { x: number; y: number; w: number; h: number };
type DragMode =
  | 'idle'
  | 'creating'
  | 'moving'
  | 'resize-tl'
  | 'resize-tr'
  | 'resize-bl'
  | 'resize-br';

/** Crop an image (data URL) to a fractional rect → cropped data URL. */
function cropImage(src: string, rect: CropRect): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const sx = Math.round(rect.x * img.naturalWidth);
      const sy = Math.round(rect.y * img.naturalHeight);
      const sw = Math.max(1, Math.round(rect.w * img.naturalWidth));
      const sh = Math.max(1, Math.round(rect.h * img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export default function StepConfirmCutout({
  productFile,
  productImage,
  croppedImage,
  onCutoutChange,
  compact,
}: StepConfirmCutoutProps) {
  // Original product as a data URL (the crop source). Derived from the
  // File so we don't depend on the parent passing a preview.
  const [originalUrl, setOriginalUrl] = useState<string | null>(productImage ?? null);
  const fileRef = useRef<File | null>(null);

  const [cropping, setCropping] = useState(false);
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const [dragMode, setDragMode] = useState<DragMode>('idle');
  const dragStartRef = useRef<{ x: number; y: number; rect: CropRect } | null>(null);
  const cropAreaRef = useRef<HTMLDivElement>(null);

  // Load the original product as a data URL whenever the file changes.
  useEffect(() => {
    if (!productFile) {
      setOriginalUrl(null);
      return;
    }
    if (fileRef.current === productFile && originalUrl) return;
    fileRef.current = productFile;
    const reader = new FileReader();
    reader.onload = () => setOriginalUrl(reader.result as string);
    reader.readAsDataURL(productFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFile]);

  // Default: the full image flows through (crop is an optional trim). Set
  // it once the original loads if the parent has nothing yet.
  useEffect(() => {
    if (originalUrl && !croppedImage) onCutoutChange(originalUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalUrl]);

  // The image currently shown (cropped result if any, else original).
  const displayUrl = croppedImage || originalUrl;

  const handleOpenCrop = () => {
    setCropRect({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
    setCropping(true);
  };

  const applyCrop = async () => {
    // Always crop from the ORIGINAL so re-cropping starts from the full
    // image (not a previous crop).
    if (!originalUrl || !cropRect) return;
    const cropped = await cropImage(originalUrl, cropRect);
    if (cropped) onCutoutChange(cropped);
    setCropping(false);
    setCropRect(null);
  };

  const resetCrop = () => {
    if (originalUrl) onCutoutChange(originalUrl);
  };

  // ---- Crop drag handlers (free rectangle + corner resize + move) ----
  const HANDLE_HIT_FRAC = 0.04;

  const onCropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cropping || !cropAreaRef.current) return;
    const rect = cropAreaRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (cropRect) {
      const tlDist = Math.hypot(x - cropRect.x, y - cropRect.y);
      const trDist = Math.hypot(x - (cropRect.x + cropRect.w), y - cropRect.y);
      const blDist = Math.hypot(x - cropRect.x, y - (cropRect.y + cropRect.h));
      const brDist = Math.hypot(x - (cropRect.x + cropRect.w), y - (cropRect.y + cropRect.h));
      const minDist = Math.min(tlDist, trDist, blDist, brDist);
      if (minDist < HANDLE_HIT_FRAC) {
        dragStartRef.current = { x, y, rect: { ...cropRect } };
        if (tlDist === minDist) setDragMode('resize-tl');
        else if (trDist === minDist) setDragMode('resize-tr');
        else if (blDist === minDist) setDragMode('resize-bl');
        else setDragMode('resize-br');
        return;
      }
      if (
        x >= cropRect.x &&
        x <= cropRect.x + cropRect.w &&
        y >= cropRect.y &&
        y <= cropRect.y + cropRect.h
      ) {
        setDragMode('moving');
        dragStartRef.current = { x, y, rect: { ...cropRect } };
        return;
      }
    }
    setDragMode('creating');
    dragStartRef.current = { x, y, rect: { x, y, w: 0, h: 0 } };
    setCropRect({ x, y, w: 0, h: 0 });
  };

  useEffect(() => {
    if (dragMode === 'idle') return;
    const move = (e: MouseEvent) => {
      if (!cropAreaRef.current || !dragStartRef.current) return;
      const rect = cropAreaRef.current.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width;
      const cy = (e.clientY - rect.top) / rect.height;
      const x = Math.max(0, Math.min(1, cx));
      const y = Math.max(0, Math.min(1, cy));
      const start = dragStartRef.current;

      if (dragMode === 'creating') {
        setCropRect({
          x: Math.min(start.x, x),
          y: Math.min(start.y, y),
          w: Math.abs(x - start.x),
          h: Math.abs(y - start.y),
        });
      } else if (dragMode === 'moving') {
        const dx = x - start.x;
        const dy = y - start.y;
        setCropRect({
          x: Math.max(0, Math.min(1 - start.rect.w, start.rect.x + dx)),
          y: Math.max(0, Math.min(1 - start.rect.h, start.rect.y + dy)),
          w: start.rect.w,
          h: start.rect.h,
        });
      } else {
        const opp =
          dragMode === 'resize-tl'
            ? { x: start.rect.x + start.rect.w, y: start.rect.y + start.rect.h }
            : dragMode === 'resize-tr'
              ? { x: start.rect.x, y: start.rect.y + start.rect.h }
              : dragMode === 'resize-bl'
                ? { x: start.rect.x + start.rect.w, y: start.rect.y }
                : { x: start.rect.x, y: start.rect.y };
        setCropRect({
          x: Math.min(opp.x, x),
          y: Math.min(opp.y, y),
          w: Math.abs(x - opp.x),
          h: Math.abs(y - opp.y),
        });
      }
    };
    const up = () => {
      setDragMode('idle');
      dragStartRef.current = null;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [dragMode]);

  // ---- Render ----
  return (
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-crop-line"
          title="Crop your product"
          subtitle="Trim to the product so it fills the design. Cropping is optional — we'll place it cleanly into the template either way."
        />
      )}

      {!productFile ? (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-amber-500 mb-2" />
          <p className="text-amber-800 text-sm font-semibold mb-1">No product uploaded yet</p>
          <p className="text-amber-700 text-xs">Go back to the Upload step to add a product image.</p>
        </div>
      ) : cropping ? (
        // ---- CROP TOOL — drag a rectangle over the original product ----
        <div className="flex flex-col gap-4">
          <div
            ref={cropAreaRef}
            onMouseDown={onCropMouseDown}
            className="relative w-full max-w-md mx-auto aspect-square overflow-hidden rounded-2xl border-2 border-primary/40 select-none bg-surface-dark/30"
            style={{ cursor: 'crosshair' }}
          >
            {originalUrl && (
              <img
                src={originalUrl}
                alt="Product"
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              />
            )}
            {cropRect && (
              <>
                <div
                  className="absolute inset-0 pointer-events-none bg-dark/55"
                  style={{
                    clipPath: `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${cropRect.x * 100}% ${cropRect.y * 100}%, ${cropRect.x * 100}% ${(cropRect.y + cropRect.h) * 100}%, ${(cropRect.x + cropRect.w) * 100}% ${(cropRect.y + cropRect.h) * 100}%, ${(cropRect.x + cropRect.w) * 100}% ${cropRect.y * 100}%, ${cropRect.x * 100}% ${cropRect.y * 100}%)`,
                  }}
                />
                <div
                  className="absolute border-2 border-white pointer-events-none"
                  style={{
                    left: `${cropRect.x * 100}%`,
                    top: `${cropRect.y * 100}%`,
                    width: `${cropRect.w * 100}%`,
                    height: `${cropRect.h * 100}%`,
                  }}
                >
                  {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
                    <span
                      key={c}
                      className="absolute w-3 h-3 bg-white border border-primary rounded-sm"
                      style={{
                        left: c === 'tl' || c === 'bl' ? -6 : undefined,
                        right: c === 'tr' || c === 'br' ? -6 : undefined,
                        top: c === 'tl' || c === 'tr' ? -6 : undefined,
                        bottom: c === 'bl' || c === 'br' ? -6 : undefined,
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => {
                setCropping(false);
                setCropRect(null);
              }}
              className="px-4 py-2 rounded-full border border-surface-dark text-dark-muted hover:text-dark text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={applyCrop}
              className="px-5 py-2 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-hover"
            >
              Apply crop
            </button>
          </div>
        </div>
      ) : (
        // ---- RESULT — show the cropped product at its natural aspect ----
        <div className="flex flex-col gap-4">
          <div className="w-full flex items-center justify-center rounded-2xl bg-surface-dark/30 p-4">
            {displayUrl && (
              <img
                src={displayUrl}
                alt="Product"
                className="max-w-full max-h-[52vh] object-contain rounded-lg"
              />
            )}
          </div>
          <div className="flex justify-center gap-2">
            <button
              onClick={handleOpenCrop}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-hover"
            >
              <i className="ri-crop-line" />
              {croppedImage && croppedImage !== originalUrl ? 'Re-crop' : 'Crop product'}
            </button>
            {croppedImage && croppedImage !== originalUrl && (
              <button
                onClick={resetCrop}
                className="px-4 py-2 rounded-full border border-surface-dark text-dark-muted hover:text-dark text-sm font-semibold"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
